import { inject, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { from, map, Observable, take } from 'rxjs';
import {
  COPY_TOKEN,
  CopyProvider,
  DateFormats,
  formatDateToString,
  MODAL_TOKEN,
  ModalProvider,
  USER_DATA_TOKEN,
  UserDataProvider,
  UserModel
} from '@core';
import { MessageContextStorage } from '../../../../application/context-storage';
import { MessageViewModel } from '../../../../application/interfaces';
import {
  STREAM_CHANNEL_TOKEN,
  STREAM_CHAT_CLIENT_TOKEN,
  StreamChannelProvider,
  StreamChatClientProvider
} from '@messages/infra';
import { ImagePreviewComponent } from '../../image-preview/image-preview.component';

const REACTION_TYPES: { type: string; emoji: string }[] = [
  { type: 'like', emoji: '👍' },
  { type: 'love', emoji: '❤️' },
  { type: 'haha', emoji: '😂' },
  { type: 'wow', emoji: '😮' },
  { type: 'sad', emoji: '😢' }
];

const REACTION_EMOJI_MAP: Record<string, string> = REACTION_TYPES.reduce(
  (acc: object, r: { type: string; emoji: string }) => ({ ...acc, [r.type]: r.emoji }),
  {}
);

export class MessageItemViewService {
  private readonly userDataProvider: UserDataProvider = inject(USER_DATA_TOKEN);
  private readonly copyProvider: CopyProvider = inject(COPY_TOKEN);
  private readonly modalProvider: ModalProvider = inject(MODAL_TOKEN);
  private readonly channelProvider: StreamChannelProvider = inject(STREAM_CHANNEL_TOKEN);
  private readonly chatClientProvider: StreamChatClientProvider = inject(STREAM_CHAT_CLIENT_TOKEN);
  private readonly messageContextStorage: MessageContextStorage = inject(MessageContextStorage);

  readonly reactionTypes: { type: string; emoji: string }[] = REACTION_TYPES;

  readonly currentUserId: Signal<string | undefined> = toSignal(
    this.userDataProvider.userData$.pipe(map((user: UserModel) => user?.chatSettings?.chatUserExternalId))
  );

  isOwnMessage(message: MessageViewModel): boolean {
    return message.userId === this.currentUserId();
  }

  formatTime(createdAt: Date | undefined): string {
    if (!createdAt) return '';
    return formatDateToString(new Date(createdAt), DateFormats.Time);
  }

  getReactionEmoji(type: string): string {
    return REACTION_EMOJI_MAP[type] || type;
  }

  openImagePreview(imageUrl: string): void {
    this.modalProvider
      .showModal$({
        component: ImagePreviewComponent,
        componentProps: { imageUrl },
        cssClass: 'fullscreen-modal'
      })
      .subscribe();
  }

  deleteMessage(messageId: string): void {
    from(this.chatClientProvider.deleteMessage(messageId)).pipe(take(1)).subscribe();
  }

  toggleReaction(messageId: string, reactionType: string, hasOwnReaction: boolean): void {
    const action$: Observable<void> = hasOwnReaction
      ? from(this.channelProvider.deleteReaction(messageId, reactionType))
      : from(this.channelProvider.sendReaction(messageId, reactionType));

    action$.pipe(take(1)).subscribe();
  }

  setEditingMessage(message: MessageViewModel): void {
    this.messageContextStorage.setEditingMessage(message);
  }

  setQuotedMessage(message: MessageViewModel): void {
    this.messageContextStorage.setQuotedMessage(message);
  }

  copyToClipboard(text: string): void {
    this.copyProvider.copyToClipboard(text).pipe(take(1)).subscribe();
  }
}
